import { useEffect, useMemo, useState, type FormEvent } from "react";
import { subnetInfo, ipv4Conversions } from "../lib/ipmath";

/** Runs entirely in the browser — nothing typed here is sent anywhere. */
export function SubnetCalculator() {
  const [input, setInput] = useState("192.168.1.130/26");

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("cidr");
    if (q) setInput(q);
  }, []);

  const { info, conv, error } = useMemo(() => {
    const raw = input.trim();
    if (!raw) return { info: null, conv: null, error: null };
    const m = raw.match(/^([^/\s]+)\s*(?:\/\s*(\d{1,2}))?$/);
    if (!m) return { info: null, conv: null, error: "Enter an address like 192.168.1.130/26" };
    const prefix = m[2] === undefined ? 24 : Number(m[2]);
    if (prefix > 32) return { info: null, conv: null, error: "Prefix must be between 0 and 32" };
    const i = subnetInfo(m[1], prefix);
    if (!i) return { info: null, conv: null, error: "That isn't a valid IPv4 address" };
    return { info: i, conv: ipv4Conversions(m[1]), error: null };
  }, [input]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    history.replaceState(null, "", `?${new URLSearchParams({ cidr: input.trim() })}`);
  };

  return (
    <main className="console">
      <div className="input-row" role="search">
        <span className="input-row-label">SUBNET</span>
        <form onSubmit={submit} className="lookup-form">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="192.168.1.130/26"
            autoFocus
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            aria-label="IPv4 address with CIDR prefix"
          />
          <button type="submit">Calculate</button>
        </form>
        <span className="input-row-hint" aria-hidden="true">
          <span className="stat-dot" /> instant · nothing leaves your browser
        </span>
      </div>

      {error ? <div className="error" role="alert">{error}</div> : null}

      <div className="body-grid body-grid-single">
        <section className="table-panel">
          <div className="panel-title">
            SUBNET DETAILS
            {info ? <b> · {info.cidr}</b> : null}
          </div>

          {info ? (
            <>
              <div className="subnet-grid">
                <Stat label="Network address" value={info.network} />
                <Stat label="Broadcast address" value={info.broadcast} />
                <Stat label="Usable host range" value={`${info.firstHost} – ${info.lastHost}`} wide />
                <Stat label="Subnet mask" value={info.netmask} />
                <Stat label="Wildcard mask" value={info.wildcard} />
                <Stat label="Usable hosts" value={info.usableHosts} />
                <Stat label="Total addresses" value={info.totalAddresses} />
                <Stat label="Scope" value={info.scope} />
              </div>

              <table className="health-table">
                <tbody>
                  <tr className="health-row">
                    <td className="ht-type">Mask in binary</td>
                    <td className="ht-records"><code>{info.binaryMask}</code></td>
                  </tr>
                  {conv ? (
                    <>
                      <tr className="health-row">
                        <td className="ht-type">Address as decimal</td>
                        <td className="ht-records"><code>{conv.decimal}</code></td>
                      </tr>
                      <tr className="health-row">
                        <td className="ht-type">Address as hex</td>
                        <td className="ht-records"><code>{conv.hex}</code></td>
                      </tr>
                      <tr className="health-row">
                        <td className="ht-type">Address in binary</td>
                        <td className="ht-records"><code>{conv.binary}</code></td>
                      </tr>
                    </>
                  ) : null}
                </tbody>
              </table>

              {info.prefix >= 31 ? (
                <p className="myip-note">
                  {info.prefix === 32
                    ? "A /32 is a single host — there's no network or broadcast address to reserve."
                    : "A /31 is a point-to-point link (RFC 3021). Both addresses are usable; no network or broadcast address is reserved, which is why this shows 2 usable hosts rather than 0."}
                </p>
              ) : null}

              <p className="whois-cross">
                Working with IPv6 instead? Use the <a href="/ipv6-tools">IPv6 toolkit</a> · or see{" "}
                <a href="/what-is-my-ip">your own IP</a>.
              </p>
            </>
          ) : null}
        </section>
      </div>
    </main>
  );
}

function Stat({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={`subnet-stat${wide ? " subnet-wide" : ""}`}>
      <span className="subnet-stat-label">{label}</span>
      <code className="subnet-stat-value">{value}</code>
    </div>
  );
}
